import { useState, useRef, useEffect } from "react";
import { useRemoteContent } from "@/lib/ContentContext";

interface Message {
  role: "user" | "ai";
  content: string;
}

const FAQ_ZH: Record<string, string> = {
  "产品": "我们主要提供以下产品与服务：\n1. 航空发动机研发（涡扇/涡轴/活塞）\n2. 无人机动力总成系统\n3. 精密零部件制造\n4. 技术咨询与培训服务\n如需详细了解，请告诉我您感兴趣的方向！",
  "联系": "您可以通过以下方式联系我们：\n📞 电话：+86 18176708838  微信同号\n📧 邮箱：175153762@qq.com\n📍 地址：北京市海淀区航天城科技园\n也可以填写页面上的联系表单，我们会在1个工作日内回复。",
  "价格": "我们的产品均为定制化研发，价格因方案规格不同而有所差异。建议您通过联系表单提交需求，我们的技术团队将为您提供详细报价。",
  "合作": "非常欢迎合作！请通过联系表单留下您的信息和合作意向，我们的商务团队会尽快与您取得联系。",
  "资质": "公司具备以下主要资质：\n✅ 国家高新技术企业认证\n✅ GJB军标质量管理体系\n✅ AS9100航空质量认证\n✅ 多项国家发明专利",
  "发动机": "我们在航空发动机领域拥有15年以上研发经验，覆盖涡扇、涡轴、活塞等多类型发动机，具备完整的设计、仿真、试验与适航认证能力。",
  "无人机": "我们为各类无人机平台提供电动和混动动力总成系统，包括高效电机、电调、螺旋桨优化及整体动力管理系统，支持全定制开发。",
  "default": "您好！我是航空动力科技的智能客服助手。我可以帮您了解我们的产品服务、公司资质、联系方式等信息。请问您想了解什么？",
};

const FAQ_EN: Record<string, string> = {
  "product": "We offer the following products & services:\n1. Aero Engine R&D (turbofan/turboshaft/piston)\n2. UAV Propulsion Systems\n3. Precision Parts Manufacturing\n4. Technical Consulting & Training\nFeel free to ask for more details!",
  "contact": "You can reach us via:\n📞 Phone: +86 18176708838  WeChat same number\n📧 Email: 175153762@qq.com\n📍 Address: Aerospace Tech Park, Haidian, Beijing\nOr fill the contact form and we'll reply within 1 business day.",
  "price": "Our products are fully customized, so pricing varies by specification. Please submit your requirements via the contact form and our engineering team will provide a detailed quote.",
  "cooperat": "We welcome collaboration! Please leave your info and intentions via the contact form, and our business team will reach out promptly.",
  "qualif": "Our major certifications include:\n✅ National Hi-Tech Enterprise\n✅ GJB Military Quality System\n✅ AS9100 Aviation Quality Cert.\n✅ Multiple national invention patents",
  "engine": "We have 15+ years of experience in aero engine development covering turbofan, turboshaft and piston engines, with full design, simulation, testing and airworthiness capabilities.",
  "uav": "We provide electric and hybrid propulsion systems for various UAV platforms, including high-efficiency motors, ESCs, propeller optimization and integrated power management.",
  "default": "Hello! I'm the AI assistant for Aerospace Propulsion Technology. I can help you learn about our products, qualifications, contact info, and more. What would you like to know?",
};

function getReply(msg: string, lang: "zh" | "en"): string {
  const lc = msg.toLowerCase();
  if (lang === "zh") {
    for (const key of Object.keys(FAQ_ZH)) {
      if (key !== "default" && lc.includes(key)) return FAQ_ZH[key];
    }
    return FAQ_ZH["default"];
  } else {
    for (const key of Object.keys(FAQ_EN)) {
      if (key !== "default" && lc.includes(key)) return FAQ_EN[key];
    }
    return FAQ_EN["default"];
  }
}

interface AIChatProps {
  lang: "zh" | "en";
}

export default function AIChat({ lang }: AIChatProps) {
  const { aiChat } = useRemoteContent();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "ai", content: lang === "zh" ? FAQ_ZH["default"] : FAQ_EN["default"] },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages([{ role: "ai", content: lang === "zh" ? FAQ_ZH["default"] : FAQ_EN["default"] }]);
  }, [lang, aiChat.endpoint]);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  async function getRemoteReply(text: string, history: Message[]): Promise<string> {
    if (!aiChat.endpoint.trim()) return getReply(text, lang);

    const res = await fetch(aiChat.endpoint.trim(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: text, lang, history }),
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json() as {
      reply?: string;
      message?: string;
      content?: string;
      answer?: string;
      choices?: Array<{ message?: { content?: string } }>;
    };

    return data.reply
      || data.answer
      || data.message
      || data.content
      || data.choices?.[0]?.message?.content
      || (lang === "zh" ? "接口已连接，但未返回可显示的回复内容。" : "The endpoint responded without a displayable reply.");
  }

  const sendText = async (rawText: string) => {
    const text = rawText.trim();
    if (!text) return;
    const newMsgs: Message[] = [...messages, { role: "user", content: text }];
    setMessages(newMsgs);
    setInput("");
    setTyping(true);
    try {
      const reply = await getRemoteReply(text, newMsgs);
      setMessages([...newMsgs, { role: "ai", content: reply }]);
    } catch {
      setMessages([
        ...newMsgs,
        {
          role: "ai",
          content: lang === "zh"
            ? "智能客服接口暂时不可用，已切换为离线答复模式。请稍后重试或直接提交留言。"
            : "The AI endpoint is temporarily unavailable. Please try again later or leave us a message.",
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const send = () => {
    void sendText(input);
  };

  const quickReplies = lang === "zh"
    ? ["产品介绍", "联系方式", "公司资质", "价格咨询"]
    : ["Products", "Contact Info", "Qualifications", "Pricing"];

  if (!aiChat.enabled) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-400 shadow-lg shadow-cyan-500/40 transition-all duration-300 hover:scale-110 cursor-pointer"
      >
        <i className={`text-2xl text-[#0a0f1e] transition-all duration-300 ${open ? "ri-close-line" : "ri-customer-service-2-line"}`} />
      </button>

      {/* Chat window */}
      <div
        className={`fixed bottom-24 right-6 z-50 w-80 md:w-96 bg-xh-bg-card border border-cyan-500/20 rounded-2xl shadow-2xl shadow-black/50 transition-all duration-400 overflow-hidden ${
          open ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95 pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-cyan-600/20 to-transparent border-b border-white/5">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-cyan-500/20">
            <i className="ri-robot-line text-cyan-400 text-base" />
          </div>
          <div className="flex-1">
            <p className="text-white text-sm font-semibold">
              {lang === "zh" ? "AI 智能客服" : "AI Assistant"}
            </p>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-cyan-400 text-xs">{lang === "zh" ? "在线" : "Online"}</span>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer">
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* Messages */}
        <div className="h-64 overflow-y-auto px-4 py-3 flex flex-col gap-3 scrollbar-thin">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-line ${
                  m.role === "user"
                    ? "bg-cyan-500 text-[#0a0f1e] font-medium"
                    : "bg-white/5 text-gray-300 border border-white/5"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/5 rounded-xl px-4 py-2.5 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Quick replies */}
        <div className="px-4 pb-2 flex gap-1.5 flex-wrap">
          {quickReplies.map((q, i) => (
            <button
              key={i}
              onClick={() => {
                void sendText(q);
              }}
              className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-400 text-xs hover:border-cyan-500/40 hover:text-cyan-400 transition-all cursor-pointer whitespace-nowrap"
            >
              {q}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="flex items-center gap-2 px-3 py-3 border-t border-white/5">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={lang === "zh" ? "输入您的问题..." : "Type your question..."}
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-white text-xs placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
          />
          <button
            onClick={send}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-400 text-[#0a0f1e] transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-send-plane-fill text-sm" />
          </button>
        </div>
      </div>
    </>
  );
}
